import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";
import { ExportBar } from "@/components/portal/ExportBar";
import { BillingRunner } from "@/components/control/BillingRunner";

const CONTROL_EXPORTS = ["providers", "customers", "inquiries", "packages", "subscriptions", "invoices", "documents", "activity_log", "security_log"];

function Chip({ label, value }) {
  return (
    <div className="px-3 py-2 text-center" style={{ background: "var(--surface-alt)", borderRadius: "var(--radius)" }}>
      <div className="font-heading text-lg font-bold" style={{ color: "var(--text)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
    </div>
  );
}

export function ControlReports() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const load = () => api.get("/control/reports").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader />;

  return (
    <div className="kw-fade" data-testid="control-reports">
      <h1 className="font-heading text-3xl font-bold mb-6" style={{ color: "var(--text)" }}>{t("nav.reports")}</h1>
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <BillingRunner scope="control" onDone={load} />
        <ExportBar basePath="/control/export" entities={CONTROL_EXPORTS} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((r) => {
          const chart = (r.monthly || []).map((m) => ({ name: m.month.slice(5), inquiries: m.inquiries, revenue: m.revenue }));
          return (
            <Card key={r.portal_key} className="p-5 kw-fade-up" data-testid={`report-portal-${r.portal_key}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="font-heading text-xl font-bold" style={{ color: "var(--text)" }}>{r.name}</span>
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: r.status === "live" ? "#DCFCE7" : "var(--surface-alt)", color: r.status === "live" ? "#15803D" : "var(--text-muted)" }}>{r.status === "live" ? t("control.live") : t("control.skeleton")}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <Chip label={t("control.tabProviders")} value={r.totals.providers} />
                <Chip label={t("stat.open_inquiries")} value={r.totals.open_inquiries} />
                <Chip label={t("control.revenue")} value={`${r.totals.revenue}`} />
                <Chip label={t("control.openInvoices")} value={r.totals.open_invoices} />
              </div>
              <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-muted)" }}>{t("rep.monthly")} · {t("rep.inquiries")}</div>
              <div style={{ width: "100%", minWidth: 0, height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)" }} />
                    <Bar dataKey="inquiries" radius={[3, 3, 0, 0]}>
                      {chart.map((_, i) => <Cell key={i} fill="var(--accent)" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
